import { Component } from 'react';
import { connect } from 'react-redux';
import lodash from 'lodash';

import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import { GridPanel, Grid } from 'common/components/layout/GridPanel';

import userPermissionsEditorShape from 'binder/components/UserPermissionsEditor/userPermissionsEditorShape';
import UserList from 'binder/components/UserPermissionsEditor/UserList/UserList';
import ChangePermissionsPanel from 'binder/components/UserPermissionsEditor/ChangePermissionsPanel/ChangePermissionsPanel';
import styles from 'binder/components/UserPermissionsEditor/UserPermissionsEditor.scss';
import {
    loadUsersConfiguration,
    changeSelection,
    changeSearch,
    toggleUserPermissionsEditorSetting,
    changePermissionsDelta,
    grantAccess,
    revokeAccess,
    saveChanges,
    initializeUserPermissionsEditor,
} from 'binder/actions/usersConfiguration';
import { binderInstitutionIdSelector } from 'common/selectors';

const mixed = '__mixed__';

export class UserPermissionsEditor extends Component {
    componentDidMount() {
        this.props.initializeUserPermissionsEditor();
        this.props.changeSelection('none');
        this.props.changeSearch('');
    }

    componentDidUpdate(prevProps) {
        if (this.props.binderId !== prevProps.binderId) {
            this.props.clearSelection();
        }
    }

    handleSearch(e) {
        this.props.changeSearch(e.target.value);
    }

    handleToggleViewAllUsers(e) {
        e.preventDefault();
        this.props.toggleUserPermissionsEditorSetting('viewAllUsers', e.target.value);
    }

    handleEnableSummaryChange(e) {
        e.preventDefault();
        this.props.toggleUserPermissionsEditorSetting('viewSummaryMode', e.target.value === 'true');
    }

    handleSaveChanges(permissionItems) {
        this.props.saveChanges(this.props.binderId, permissionItems);
    }

    handleCancelChanges() {
        this.props.initializeUserPermissionsEditor();
        this.props.loadUsersConfiguration(this.props.binderId);
        this.props.changeSelection('none');
        this.props.changeSearch('');
        this.props.addApplicationMessage('info', 'Changes were canceled.');
    }

    computePermissionItems(permissionProperties, relevantUsers, permissionsDelta) {
        if (lodash.isEmpty(relevantUsers)) {
            return null;
        }
        return lodash.map(permissionProperties, property => {
            const { name, displayName, allowedValues } = property;
            const value = lodash.reduce(relevantUsers, (currentValue, nextUser) => {
                const nextValue = lodash.get(nextUser, ['binderPermissions', name]);
                let finalValue = {};
                if (permissionsDelta && permissionsDelta[name]) {
                    finalValue = permissionsDelta[name];
                } else if (lodash.isNil(currentValue.value)) {
                    finalValue = nextValue.value;
                } else if (nextValue.value === currentValue.value) {
                    finalValue = nextValue.value;
                } else {
                    finalValue = mixed;
                }
                currentValue.value = finalValue;
                currentValue.isRestricted = nextValue.isRestricted || currentValue.isRestricted;
                currentValue.allRestricted = nextValue.isRestricted && currentValue.allRestricted;
                return currentValue;
            }, {
                value: null,
                isRestricted: false,
                allRestricted: true,
            });
            return {
                name: name,
                displayName: displayName,
                allowedValues,
                ...value,
            };
        });
    }

    render() {
        const {
            searchString,
            viewAllUsers,
            viewSummaryMode,
            updatedUsers,
            unsavedChanges,
            users,
            permissionProperties,
            selectedUsers,
            permissionsDelta,
            atLeastOneHasDelta,
        } = this.props;
        const handleSearch = lodash.throttle(this.handleSearch.bind(this), 50);
        const handleToggleViewAllUsers = lodash.throttle(this.handleToggleViewAllUsers.bind(this), 50);
        const handleEnableSummaryChange = lodash.throttle(this.handleEnableSummaryChange.bind(this), 50);
        const saveChangesEnabled = unsavedChanges || updatedUsers.length > 0;
        const cancelChangesEnabled = unsavedChanges || atLeastOneHasDelta;
        const relevantUsers = lodash.filter(users, u => selectedUsers.includes(u.sub));
        const permissionItems = this.computePermissionItems(permissionProperties, relevantUsers, permissionsDelta);

        return (
            <div className={styles.userPermissionsEditor} data-cy="UserPermissionsEditor">
                <Grid
                    gridRows={['auto', 1, 'auto']}
                    gridColumns={[75, 'minmax(280px,25fr)']}
                    className={styles.userPermissionsGrid}
                    display='grid'>
                    <GridPanel gridRow={1} gridColumn={1} gridColumnSpan={2}>
                        <UserSearch
                            searchString={searchString}
                            viewAllUsers={viewAllUsers}
                            viewSummaryMode={viewSummaryMode}
                            handleSearch={handleSearch}
                            handleToggleViewAllUsers={handleToggleViewAllUsers}
                            handleEnableSummaryChange={handleEnableSummaryChange}/>
                    </GridPanel>
                    <GridPanel
                        className={styles.userListPanel}
                        gridRow={2}
                        gridColumn={1}>
                        <UserList {...this.props} />
                    </GridPanel>
                    <GridPanel
                        className={styles.changePermissionsPanel}
                        gridRow={2}
                        gridColumn={2}>
                        <ChangePermissionsPanel {...this.props} permissionItems={permissionItems}/>
                    </GridPanel>
                    <GridPanel gridRow={3} gridColumn={1} gridColumnSpan={2}>
                        <StatusBar
                            updatedUsers={updatedUsers}
                            handleSaveChanges={this.handleSaveChanges.bind(this, permissionItems)}
                            handleCancelChanges={this.handleCancelChanges.bind(this)}
                            saveChangesEnabled={saveChangesEnabled}
                            cancelChangesEnabled={cancelChangesEnabled}/>
                    </GridPanel>
                </Grid>
            </div>
        );
    }
}

UserPermissionsEditor.propTypes = userPermissionsEditorShape;

export const UserSearch = props => {
    return (
        <Grid
            gridRows={1}
            gridColumns={['auto', 1, 'auto']}
            className={styles.userPermissionsSearchBar}
            display='grid'>
            <GridPanel gridRow={1} gridColumn={1}>
                <div className={styles.searchIcon}/>
            </GridPanel>
            <GridPanel gridRow={1} gridColumn={2}>
                <input
                    className={styles.searchTextBox} type="text"
                    value={props.searchString}
                    onChange={props.handleSearch}
                    placeholder='Search by user or group'/>
            </GridPanel>
            <GridPanel gridRow={1} gridColumn={3}>
                <ViewingOptions
                    viewAllUsers={props.viewAllUsers}
                    viewSummaryMode={props.viewSummaryMode}
                    handleToggleViewAllUsers={props.handleToggleViewAllUsers}
                    handleEnableSummaryChange={props.handleEnableSummaryChange}/>
            </GridPanel>
        </Grid>
    );
};

export const ViewingOptions = props => {
    return (
        <Grid
            gridRows={1}
            gridColumns={[1, 1, 1, 1]}
            className={styles.viewingOptions}
            columnItemAlignment='center'
            display='grid'>
            <GridPanel
                className={styles.viewOption}
                gridRow={1}
                gridColumn={1}>
                <span className={styles.viewLabel}>Show all users</span>
            </GridPanel>
            <GridPanel
                className={styles.viewOption}
                gridRow={1}
                gridColumn={2}>
                <select
                    className={styles.viewSelect}
                    value={props.viewAllUsers}
                    onChange={props.handleToggleViewAllUsers}>
                    <option value="all">I can access</option>
                    <option value="in">In binder</option>
                    <option value="out">Not in binder</option>
                </select>
            </GridPanel>
            <GridPanel
                className={styles.viewOption}
                gridRow={1}
                gridColumn={3}
                rowAlign='flex-end'>
                <span className={styles.viewLabel}>View</span>
            </GridPanel>
            <GridPanel
                className={styles.viewOption}
                gridRow={1}
                gridColumn={4}>
                <select
                    className={styles.viewSelect}
                    value={props.viewSummaryMode}
                    onChange={props.handleEnableSummaryChange}>
                    <option value={true}>Summary</option>
                    <option value={false}>Detailed</option>
                </select>
            </GridPanel>
        </Grid>
    );
};

export const StatusBar = props => {
    let statusBarMessage;
    const {
        saveChangesEnabled,
        cancelChangesEnabled,
        handleSaveChanges,
        handleCancelChanges,
        updatedUsers,
    } = props;
    if (updatedUsers.length > 0) {
        statusBarMessage = `Changed ${updatedUsers.length} users. Click Save Changes to commit these changes.`;
    } else {
        statusBarMessage = 'Click on a user to add them to the selection. Click on them again to remove.';
    }

    return (
        <Grid
            gridRows={1}
            gridColumns={[75, 'minmax(280px,25fr)']}
            className={styles.statusBar}
            columnItemAlignment={'center'}
            display='grid'>
            <GridPanel gridRow={1} gridColumn={1}>
                <span className={styles.statusBarInstructions}>{statusBarMessage}</span>
            </GridPanel>
            <GridPanel gridRow={1} gridColumn={2} className={styles.statusBarButtonList}>
                <button
                    className={styles.statusBarButton}
                    disabled={!cancelChangesEnabled}
                    onClick={handleCancelChanges}>Cancel Changes
                </button>
                <button
                    className={styles.statusBarButton}
                    disabled={!saveChangesEnabled}
                    onClick={handleSaveChanges}>Save Changes
                </button>
            </GridPanel>
        </Grid>
    );
};

export default connect(state => {
    const userPermissionsEditor = lodash.get(state, 'userPermissionsEditor', {});
    const {
        selectedUsers,
        visibleUsers,
        updatedUsers,
        searchString,
        unsavedChanges,
        isSaving,
        viewAllUsers,
        viewSummaryMode,
        permissionsDelta,
    } = userPermissionsEditor;
    const {
        sub: currentUserSub,
    } = state && state.me;
    const institutionId = binderInstitutionIdSelector(state);
    return {
        selectedUsers: selectedUsers || [],
        visibleUsers: visibleUsers || [],
        updatedUsers: updatedUsers || [],
        searchString: searchString || '',
        unsavedChanges: lodash.isNil(unsavedChanges) ? false : unsavedChanges,
        atLeastHasOneDelta: !lodash.isEmpty(permissionsDelta),
        isSaving: lodash.isNil(isSaving) ? false : isSaving,
        viewAllUsers: lodash.isNil(viewAllUsers) ? 'all' : viewAllUsers,
        viewSummaryMode: lodash.isNil(viewSummaryMode) ? true : viewSummaryMode,
        permissionsDelta: lodash.isNil(permissionsDelta) ? {} : permissionsDelta,
        currentUserSub: lodash.isNil(currentUserSub) ? null : currentUserSub,
        institutionId,
    };
}, {
    changeSelection,
    changeSearch,
    toggleUserPermissionsEditorSetting,
    changePermissionsDelta,
    grantAccess,
    revokeAccess,
    initializeUserPermissionsEditor,
    loadUsersConfiguration,
    saveChanges,
    addApplicationMessage,
})(UserPermissionsEditor);
